import React from 'react';
import { useFormikContext, FieldArray, getIn } from 'formik';
import * as FormikFields from 'formik-material-ui';
import i18n from 'i18n-js';
import Button from '@material-ui/core/Button';
import {
    TextField,
    InputAdornment,
    IconButton,
    FilledInput as NativeInput,
    InputLabel,
    FormControl,
    Box,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import clsx from 'clsx';

import { AddIngredientsModal, Input } from 'components/shared';
import { IngredientModel } from 'api/models';
import { images } from 'assets';
import { SectionData } from 'store/menuDetailsStore/reducer';
import { truncateString } from 'helpers/helperFunctions';
import ImageContainer, {
    ImageContainerRefProps,
} from 'components/shared/components/ImageContainer';
import AllergensSelect from 'components/shared/components/AllergensSelect';
import ServiceTaxSelect from '../../ServiceTaxSelect';
import {
    OrderServices,
    TaxType,
    SpicinessLevels,
    Diets,
    DietaryLaws,
    MenuType,
} from 'constants/enums';
import { useStyles } from '../../../styles';
import { useAppSelector } from 'index';
import { regexHelper } from 'helpers/regexHelper';
import { getAllPricingLevels } from 'helpers/pricingLevelHelper';
import { getDefaultCustomOffer, getDefaultSameDayOffer } from 'helpers/orderInAdvanceHelper';
import { getSpicinessLevels, getDiets, getDietaryLaws } from 'constants/maps';
import DishDietarySelect from 'components/shared/components/DishDietarySelect';
import LabelSelect from 'components/shared/components/LabelSelect';
import { theme } from 'config/theme';
import { Positions } from 'constants/enums';

export interface MainTabProps {
    sections: SectionData[];
    disabled: boolean;
    restaurantName?: string;
    showAdditionalForm: boolean;
    title: React.RefObject<HTMLDivElement>;
    showAddIngredientsModal: boolean;
    setShowAddIngredientsModal: (v: boolean) => void;
    setTextToEdit: (t: string) => void;
    setValueName: (n: string) => void;
    setShowEditor: (v: boolean) => void;
    childRef: React.RefObject<ImageContainerRefProps>;
    isDeniedTaxSetup: boolean;
    isFieldDisabled: (name: string, additionalOption?: boolean) => boolean;
    defaultIngredients: IngredientModel[];
}

export default function MainTab(props: MainTabProps) {
    const classes = useStyles();
    const { values, touched, errors, setFieldValue } = useFormikContext<any>();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const taxes = useAppSelector((state) => state.restaurantDetails.taxes);
    const menu = useAppSelector((state) => state.menuDetails.menu);
    const categoryDetails = useAppSelector((state) => state.category.categoryDetails);
    const user = useAppSelector((state) => state.user);
    const currentRestaurant = user.restaurants?.find((x) => x.id === user.currentRestaurantId);
    const currentPosition = currentRestaurant?.currentPosition;
    const isCatalog = menu.type === MenuType.Catalog;
    const isCatalogManager =
        (menu.type === MenuType.Catalog &&
            currentPosition === Positions.MANAGER &&
            menu.catalogData?.allowToEditCatalog) ??
        false;

    function addToAllergens(
        ingredients: IngredientModel[],
        setFieldValueFn: (key: string, value: any) => void
    ) {
        const newAllergens: string[] = [...values.allergens];
        ingredients.forEach((eachIngredient) => {
            eachIngredient.allergens.forEach((allergen) => {
                if (!newAllergens.includes(allergen)) {
                    newAllergens.push(allergen);
                }
            });
        });
        setFieldValueFn('allergens', newAllergens);
    }

    return (
        <>
            <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between">
                <Box display="flex" flexDirection="column" width="60%">
                    <Box className={classes.photoContainer}>
                        <ImageContainer
                            disabled={props.isFieldDisabled('photo')}
                            ref={props.childRef}
                            photo={values.photo}
                            onChange={(selected, fileUrl) => {
                                setFieldValue('photoSelected', selected);
                                setFieldValue('photo', fileUrl);
                            }}
                            styles={{ marginBottom: 0, height: 223 }}
                        />
                    </Box>
                    <Box
                        visibility={props.isFieldDisabled('photo') ? 'hidden' : 'visible'}
                        textAlign="start"
                        padding="8px 0 0 0"
                    >
                        <Input
                            disabled={!values.photoSelected}
                            component={FormikFields.CheckboxWithLabel}
                            variant="filled"
                            checked={values.addToGallery}
                            name="addToGallery"
                            type="checkbox"
                            Label={{ label: i18n.t('form.addPhotoToGallery') }}
                            placeholder={i18n.t('form.addPhotoToGallery')}
                            color="primary"
                        />
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column" width="37%">
                    <div>
                        <ServiceTaxSelect
                            service={OrderServices.DineIn}
                            selectedTax={values.dineInTax}
                            taxes={taxes}
                            onSelect={(tax: TaxType) => setFieldValue('dineInTax', tax)}
                            disabled={
                                !!props.restaurantName ||
                                (props.disabled || props.isDeniedTaxSetup || isCatalog
                                    ? !isCatalogManager
                                    : !restaurant.dineIn.isActive)
                            }
                            error={errors.dineInTax as string | undefined}
                        />
                        <ServiceTaxSelect
                            service={OrderServices.TakeAway}
                            selectedTax={values.takeAwayTax}
                            taxes={taxes}
                            onSelect={(tax: TaxType) => setFieldValue('takeAwayTax', tax)}
                            disabled={
                                !!props.restaurantName ||
                                (props.disabled || props.isDeniedTaxSetup || isCatalog
                                    ? !isCatalogManager
                                    : !restaurant.takeAway.isActive)
                            }
                            error={errors.takeAwayTax as string | undefined}
                        />
                        <ServiceTaxSelect
                            service={OrderServices.HomeDelivery}
                            selectedTax={values.homeDeliveryTax}
                            taxes={taxes}
                            onSelect={(tax: TaxType) => setFieldValue('homeDeliveryTax', tax)}
                            styles={{ marginBottom: 0 }}
                            disabled={
                                !!props.restaurantName ||
                                (props.disabled || props.isDeniedTaxSetup || isCatalog
                                    ? !isCatalogManager
                                    : !restaurant.homeDelivery.isActive)
                            }
                            error={errors.homeDeliveryTax as string | undefined}
                        />
                    </div>
                    <Box display="flex" marginTop={1}>
                        <Input
                            component={FormikFields.CheckboxWithLabel}
                            disabled={props.isFieldDisabled('notFood')}
                            variant="filled"
                            name="notFood"
                            type="checkbox"
                            Label={{ label: i18n.t('form.notFood') }}
                            placeholder={i18n.t('form.notFood')}
                            inputProps={{ 'aria-label': 'secondary checkbox' }}
                            color="primary"
                        />
                        <Input
                            component={FormikFields.CheckboxWithLabel}
                            disabled={props.isFieldDisabled('isCustomPrice')}
                            variant="filled"
                            name="isCustomPrice"
                            type="checkbox"
                            Label={{ label: i18n.t('common.customPrice') }}
                            placeholder={i18n.t('common.customPrice')}
                            inputProps={{ 'aria-label': 'secondary checkbox' }}
                            color="primary"
                        />
                    </Box>
                </Box>
            </Box>

            <Box
                display="flex"
                flexDirection="row"
                width="100%"
                justifyContent="space-between"
                marginTop={'7px'}
            >
                <Box display="flex" flexDirection="column" width="60%">
                    <Input
                        disabled={props.isFieldDisabled('name')}
                        variant="filled"
                        fullWidth={true}
                        name="name"
                        type="text"
                        error={errors.name}
                        label={i18n.t('form.itemName')}
                        placeholder={i18n.t('form.itemNamePlaceholder')}
                    />
                    <Input
                        disabled={props.isFieldDisabled('shortName')}
                        variant="filled"
                        name="shortName"
                        error={errors.shortName}
                        type="text"
                        label={i18n.t('form.shortName')}
                        placeholder={i18n.t('form.shortNamePlaceholder')}
                    />
                </Box>
                <Box display="flex" flexDirection="column" width="37%">
                    <Autocomplete
                        id="section"
                        disabled={true}
                        options={props.sections.map((x) => x.id)}
                        getOptionLabel={(option) =>
                            props.sections.find((x) => x.id === option)?.name ?? ''
                        }
                        value={values.section}
                        getOptionSelected={(option, value) => option === value}
                        onChange={(e, value) => {
                            setFieldValue('section', value || '');
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                name="section"
                                variant="filled"
                                fullWidth
                                label={
                                    Boolean(touched.section && errors.section)
                                        ? i18n.t('form.errors.required', { name: 'Section' })
                                        : i18n.t('common.section')
                                }
                                error={Boolean(touched.section && errors.section)}
                            />
                        )}
                    />
                    <Autocomplete
                        id="category"
                        disabled={props.isFieldDisabled('category')}
                        options={categoryDetails?.categories?.map((x) => x.id) || []}
                        getOptionLabel={(option) =>
                            categoryDetails?.categories?.find((x) => x.id === option)?.name ?? ''
                        }
                        value={values.category}
                        getOptionSelected={(option, value) => option === value}
                        onChange={(e, value) => setFieldValue('category', value || '')}
                        renderInput={(params) => (
                            <>
                                <TextField
                                    {...params}
                                    name="categories"
                                    label={
                                        Boolean(touched.category && errors.category)
                                            ? i18n.t('form.errors.required', {
                                                  name: 'Work Area',
                                              })
                                            : i18n.t('restaurant.workArea')
                                    }
                                    variant="filled"
                                    fullWidth
                                    error={Boolean(touched.category && errors.category)}
                                />
                            </>
                        )}
                    />
                </Box>
            </Box>

            <Input
                fullWidth
                disabled={props.isFieldDisabled('shortDescription')}
                variant="filled"
                name="shortDescription"
                error={errors.shortDescription}
                type="text"
                label={i18n.t('form.shortDescription')}
                placeholder={i18n.t('form.shortDescriptionPlaceholder')}
            />

            <FormControl
                fullWidth
                variant="filled"
                style={{ textAlign: 'start' }}
                disabled={props.isFieldDisabled('description')}
            >
                <InputLabel htmlFor="filled-adornment-description">
                    {i18n.t('form.description')}
                </InputLabel>
                <NativeInput
                    id="filled-adornment-description"
                    type="text"
                    onClick={() => {
                        props.setTextToEdit(values.description);
                        props.setValueName('description');
                        props.setShowEditor(true);
                    }}
                    readOnly
                    value={truncateString(
                        values.description.replace(regexHelper.removeAllTags, ''),
                        55
                    )}
                    endAdornment={
                        (!isCatalog && !props.disabled) ||
                        (isCatalog && !props.restaurantName && isCatalogManager) ? (
                            <InputAdornment position="end">
                                <IconButton aria-label="pen">
                                    <img
                                        src={images.icons.penBlack}
                                        alt="pen-black"
                                        className={classes.resetSearchIcon}
                                    />
                                </IconButton>
                            </InputAdornment>
                        ) : null
                    }
                />
            </FormControl>

            <Box component="h6" className={classes.dishFormSectionTitle}>
                {i18n.t('common.sizesNBasePrices')}
            </Box>

            <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between">
                <Input
                    className={clsx(classes.formEdit, {
                        [classes.formEditExtended]: props.showAdditionalForm,
                    })}
                    disabled={props.isFieldDisabled('size')}
                    variant="filled"
                    name="size"
                    type="text"
                    error={errors.size}
                    label={i18n.t('common.size')}
                    placeholder={
                        !!values.additionalSizes.length
                            ? i18n.t('common.sizeRequired')
                            : i18n.t('common.sizeOptional')
                    }
                />
                <Input
                    className={clsx(classes.formEdit, {
                        [classes.formEditExtended]: props.showAdditionalForm,
                        [classes.formValueBlurred]: menu.type === MenuType.MultiCourse,
                    })}
                    disabled={props.isFieldDisabled('price', values.isCustomPrice)}
                    variant="filled"
                    name="price"
                    type="text"
                    error={errors.price}
                    label={i18n.t('form.price')}
                    placeholder={i18n.t('form.price')}
                />
            </Box>

            <FieldArray
                name="additionalSizes"
                render={(arrayHelpers) => (
                    <>
                        {values.additionalSizes.map((size: any, index: number) => {
                            const sizeName = `additionalSizes[${index}].size`;
                            const sizeError = getIn(errors, sizeName);
                            const priceName = `additionalSizes[${index}].price`;
                            const priceError = getIn(errors, priceName);
                            return (
                                <div className={classes.formWrapper} key={index}>
                                    <Input
                                        className={clsx(classes.formEdit, {
                                            [classes.formEditExtended]: props.showAdditionalForm,
                                        })}
                                        disabled={
                                            props.disabled || (isCatalog && !isCatalogManager)
                                        }
                                        variant="filled"
                                        name={sizeName}
                                        type="text"
                                        error={sizeError}
                                        label={i18n.t('common.size')}
                                        placeholder={i18n.t('common.sizeRequired')}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment
                                                    position="start"
                                                    onClick={() =>
                                                        !props.disabled &&
                                                        arrayHelpers.remove(index)
                                                    }
                                                >
                                                    <IconButton aria-label="pen">
                                                        <RemoveCircleOutlineIcon
                                                            fontSize="small"
                                                            style={{
                                                                color: 'rgba(0, 0, 0, 0.54)',
                                                            }}
                                                        />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Input
                                        className={clsx(classes.formEdit, {
                                            [classes.formEditExtended]: props.showAdditionalForm,
                                            [classes.formValueBlurred]:
                                                menu.type === MenuType.MultiCourse,
                                        })}
                                        disabled={
                                            props.disabled ||
                                            values.isCustomPrice ||
                                            menu.type === MenuType.MultiCourse ||
                                            (isCatalog && !isCatalogManager)
                                        }
                                        variant="filled"
                                        name={priceName}
                                        type="text"
                                        error={priceError}
                                        label={i18n.t('form.price')}
                                        placeholder={i18n.t('form.price')}
                                    />
                                </div>
                            );
                        })}
                        {((menu.type !== MenuType.MultiCourse && !isCatalog && !props.disabled) ||
                            (isCatalog && isCatalogManager)) &&
                            !!!props.restaurantName && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={clsx(classes.addMoreSizesButton, {
                                        [classes.addMoreSizesButtonExtended]:
                                            props.showAdditionalForm,
                                    })}
                                    style={{
                                        width: props.showAdditionalForm
                                            ? props.title.current
                                                ? props.title.current.offsetWidth - 34
                                                : '100%'
                                            : '100%',
                                    }}
                                    onClick={() => {
                                        arrayHelpers.push({
                                            size: '',
                                            price: 0,
                                            priceLevels: getAllPricingLevels(
                                                restaurant,
                                                isCatalog
                                            ).map((level) => ({
                                                priceLevelId: level.id,
                                                price: 0,
                                            })),
                                            ...(restaurant.isOrderInAdvance
                                                ? {
                                                      orderInAdvanceSameDay: getDefaultSameDayOffer(
                                                          restaurant.takeAway?.workingHours
                                                      ),
                                                      orderInAdvanceCustom: getDefaultCustomOffer(
                                                          restaurant.takeAway?.workingHours
                                                      ),
                                                  }
                                                : {}),
                                        });
                                    }}
                                >
                                    {`+ ${i18n.t('common.moreSizes').toUpperCase()}`}
                                </Button>
                            )}
                    </>
                )}
            />

            <Box
                borderBottom={`1px solid ${theme.palette.divider}`}
                marginTop={2}
                marginBottom={2}
            />

            <Box className={classes.formWrapper}>
                <Box display="flex" flexDirection="column" width="48%">
                    <AllergensSelect
                        values={values.allergens}
                        onChange={(vals: string[]) => setFieldValue('allergens', vals)}
                        styles={{ marginBottom: 28 }}
                        disabled={props.isFieldDisabled('allergens')}
                    />
                </Box>
                <Box display="flex" flexDirection="column" width="48%">
                    <Button
                        variant="outlined"
                        color="primary"
                        className={classes.manageIngredientButton}
                        disabled={props.isFieldDisabled('ingredients')}
                        onClick={() =>
                            !props.isFieldDisabled('ingredients') &&
                            props.setShowAddIngredientsModal(true)
                        }
                    >
                        {`+ ${i18n.t('restaurant.manageIngredients').toUpperCase()}`}
                    </Button>
                    <AddIngredientsModal
                        defaultIngredients={props.defaultIngredients}
                        isVisible={props.showAddIngredientsModal}
                        onClose={() => {
                            props.setShowAddIngredientsModal(false);
                        }}
                        onSave={(ingredients) => {
                            addToAllergens(ingredients, setFieldValue);
                            setFieldValue('ingredients', ingredients);
                            props.setShowAddIngredientsModal(false);
                        }}
                    />
                </Box>
            </Box>

            <Box display="flex">
                <DishDietarySelect<SpicinessLevels>
                    name={i18n.t('form.spicinessLevel')}
                    value={values.spicinessLevel}
                    map={getSpicinessLevels()}
                    disabled={props.isFieldDisabled('spicinessLevel')}
                    onChange={(value: SpicinessLevels) => setFieldValue('spicinessLevel', value)}
                />
                <DishDietarySelect<Diets>
                    name={i18n.t('form.diets')}
                    value={values.diet}
                    map={getDiets()}
                    disabled={props.isFieldDisabled('diet')}
                    onChange={(value: Diets) => setFieldValue('diet', value)}
                />
                <LabelSelect
                    selectedLabels={values.labels}
                    onChange={(labels: string[]) => setFieldValue('labels', labels)}
                    disabled={props.isFieldDisabled('labels')}
                />
                <DishDietarySelect<DietaryLaws>
                    name={i18n.t('form.dietaryLaws')}
                    value={values.dietaryLaw}
                    map={getDietaryLaws()}
                    disabled={props.isFieldDisabled('dietaryLaw')}
                    onChange={(value: DietaryLaws) => setFieldValue('dietaryLaw', value)}
                />
            </Box>
        </>
    );
}
